import React, { useState } from 'react';
import { Tooltip, IconButton } from 'design-system';
import { makeStyles } from 'react-scale/components/design-system/styles';
import { Paper } from 'design-system/Paper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface HighlightIssuesProps {
  text: string;
  errors: any[];
}

const HighlightIssues: React.FC<HighlightIssuesProps> = ({ text, errors }) => {
  const errorPhrases = errors.map(error => error.text.toLowerCase());
  const classes = useStyles();
  const [showAll, setShowAll] = useState(true);

  const highlightWords = (text: string, highlightedWords: string[]) => {
    const lowerCaseText = text.toLowerCase();
    // grab all indexes of where the phrases are located and sort them
    const indexes = highlightedWords
      .map(word => {
        const index = lowerCaseText.indexOf(word);
        return [index, word];
      })
      .filter(([index, _word]) => index !== -1);

    const sortedIndexes = indexes.sort((a: any, b: any) => a[0] - b[0]);

    // split text into an array where we essentially alternate between adding non highlighted text + highlighted text
    const splitText: string[] = [];
    let currIndex = 0;
    sortedIndexes.forEach(([index, phrase]: any) => {
      if (index !== currIndex) {
        splitText.push(text.substring(currIndex, index));
      }
      splitText.push(text.substring(index, index + phrase.length));
      currIndex = index + phrase.length;
    });
    // incase theres still text after all highlighted text we manually add
    if (currIndex !== text.length) {
      splitText.push(text.substring(currIndex, text.length));
    }
    return splitText.map((part, index) => {
      if (highlightedWords.includes(part?.toLowerCase())) {
        const entry = errors.filter(error => error.text.toLowerCase() === part.toLowerCase())[0];

        return (
          <Tooltip
            title={
              <>
                <div>{`Type: ${entry.type}`}</div> <div>{`Why: ${entry.why}`}</div>
              </>
            }
            arrow
          >
            <span key={index} style={{ backgroundColor: 'yellow' }}>
              {part}
            </span>
          </Tooltip>
        );
      }
      return part;
    });
  };

  return (
    <div className={classes.container}>
      <IconButton
        onClick={() => {
          setShowAll(!showAll);
        }}
      >
        <FontAwesomeIcon icon={showAll ? 'caret-down' : 'caret-up'} />
      </IconButton>
      {showAll && (
        <Paper className={classes.textAttachment}>{highlightWords(text, errorPhrases)}</Paper>
      )}
    </div>
  );
};
const useStyles = makeStyles({
  textAttachment: {
    margin: 4,
    padding: 12,
    lineHeight: 1.6,
    '& tr': {
      border: '1px solid rgba(0, 0, 0, 0.3)',
    },
    '& tr:nth-child(even)': {
      backgroundColor: '#f2f2f2',
    },
    '& th': {
      border: '1px solid rgb(0, 0, 0, 0.3)',
      padding: '6px 10px',
      backgroundColor: '#f2f2f2',
    },
    '& td': {
      border: '1px solid rgba(0, 0, 0, 0.3)',
      padding: '10px',
    },
  },
  container: {
    marginRight: '12px',
  },
});

export default HighlightIssues;
